import makeApiCall from "../master/axiosIndex";

// Type definitions for Rate Contract
export interface ContractPrice {
  minQty: number;
  maxQty: number;
  unitPrice: number;
}

export interface ContractLine {
  uniqueLineId: string;
  categoryId: number;
  materialId: number;
  hsnCode: string;
  taxId: number;
  taxName: string;
  uomId: number;
  taxPercentage: number;
  contractPrices: ContractPrice[];
}

export interface Site {
  siteId: number;
}

export interface FileData {
  order: number;
  module: string;
  moduleFileType:
    | "attachments"
    | "termsAndConditions"
    | "instructions"
    | "lineAttachments";
  uniqueLineId?: string;
}

export interface RateContractPayload {
  contractReferenceId: string;
  contractFor: "Material" | "Service";
  description: string;
  supplierId: number;
  startDate: string;
  endDate: string;
  expiryReminderDays: number;
  autoRelease: boolean;
  paymentTerms: string;
  advancePercentage?: number;
  creditDays?: number;
  leadDays: number;
  contractDate: string;
  sites: Site[];
  contractLines: ContractLine[];
  filesData: FileData[];
}

const rateContract = {
  /**
   * Create a new Rate Contract
   * POST /rate-contract/new
   * @param data - Rate contract payload
   * @param files - Array of File objects to upload
   */
  createRateContract: (
    data: RateContractPayload,
    files: File[] = []
  ): Promise<any> => {
    const formData = new FormData();

    // Append rateContract as JSON string (includes filesData)
    formData.append("rateContract", JSON.stringify(data));

    // Append files
    files.forEach((file) => {
      formData.append("files", file);
    });

    return makeApiCall<any>({
      url: "/rate-contract/new",
      method: "post",
      data: formData,
    });
  },

  /**
   * Fetch all Rate Contracts with pagination
   * GET /rate-contract/list
   */
  fetchAllRateContracts: (params?: any): Promise<any> =>
    makeApiCall<any>({
      url: "/rate-contract/list",
      method: "get",
      params,
    }),

  /**
   * Fetch Rate Contract by ID
   * GET /rate-contract/id/:id
   */
  fetchRateContract: (id: number): Promise<any> =>
    makeApiCall<any>({
      method: "get",
      url: `/rate-contract/id/${id}`,
    }),

  /**
   * Fetch Pending Approvals for Rate Contracts with pagination
   * GET /rate-contract/pending-approvals
   */
  fetchPendingApprovals: (params?: any): Promise<any> =>
    makeApiCall<any>({
      url: "/rate-contract/pending-approvals",
      method: "get",
      params,
    }),

  /**
   * Approve Rate Contract
   * PATCH /rate-contract/approve
   * @param rateContractId - Rate contract ID
   * @param files - Array of File objects (signature)
   */
  approveRateContract: (
    rateContractId: number,
    files: File[] = []
  ): Promise<any> => {
    const formData = new FormData();

    const requestBody: any = {
      rateContractId,
      reason: "Approved comments",
    };

    // Only include filesData if files are provided
    // Backend expects filesData as an array directly, not wrapped in docKeys
    if (files.length > 0) {
      requestBody.filesData = files.map((file, index) => ({
        order: index,
        module: "Rate Contracts",
        moduleFileType: file.name || "signature.png",
      }));
    }

    formData.append("request", JSON.stringify(requestBody));
    files.forEach((file) => formData.append("files", file));

    return makeApiCall<any>({
      method: "patch",
      url: `/rate-contract/approve`,
      data: formData,
    });
  },

  /**
   * Reject Rate Contract
   * PATCH /rate-contract/reject
   * @param rateContractId - Rate contract ID
   * @param reason - Rejection reason
   * @param files - Array of File objects (signature)
   */
  rejectRateContract: (
    rateContractId: number,
    reason: string,
    files: File[] = []
  ): Promise<any> => {
    const formData = new FormData();

    const requestBody: any = {
      rateContractId,
      rejectedReason: reason,
    };

    // Only include filesData if files are provided
    // Backend expects filesData as an array directly, not wrapped in docKeys
    if (files.length > 0) {
      requestBody.filesData = files.map((file, index) => ({
        order: index,
        module: "Rate Contracts",
        moduleFileType: file.name || "signature.png",
      }));
    }

    formData.append("request", JSON.stringify(requestBody));
    files.forEach((file) => formData.append("files", file));

    return makeApiCall<any>({
      method: "patch",
      url: `/rate-contract/reject`,
      data: formData,
    });
  },

  /**
   * Fetch Rate Contract Counts
   * GET /gateWay/rate-contract/counts
   */
  fetchRateContractCounts: (): Promise<any> =>
    makeApiCall<any>({
      method: "get",
      url: "/rate-contract/counts",
    }),

  /**
   * Fetch Rate Contract Statuses
   * GET /gateWay/rate-contract/statuses
   */
  fetchRateContractStatuses: (): Promise<any> =>
    makeApiCall<any>({
      method: "get",
      url: "/rate-contract/statuses",
    }),

  /**
   * Inactivate Rate Contract
   * PATCH /rate-contract/inactivate
   * @param rateContractId - Rate contract ID
   * @param reason - Reason for inactivation
   */
  inactivateRateContract: (
    rateContractId: number,
    reason: string
  ): Promise<any> =>
    makeApiCall<any>({
      method: "patch",
      url: `/rate-contract/inactivate`,
      data: {
        rateContractId,
        reason,
      },
    }),
};

export default rateContract;
